import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { AtSign, Bell, BellOff, CircleUserRound, Flag, ImagePlay, Mic, Send, MoreHorizontal, Pencil, Phone, Pin, Search, Smile, Sticker as StickerIcon, Users, X } from 'lucide-react'
import { MediaPicker } from './MediaPicker'
import { usePicker } from './usePicker'
import { ContextMenu, MenuItem } from '../ContextMenu'
import { PinnedPanel } from './PinnedPanel'
import { customEmojiToken, insertToken, searchUnicode } from '@/lib/emoji'
import { useGifsEnabled } from '@/lib/gifsApi'
import { emojiImageUrl } from '@/lib/markdown'
import { useGuildStore } from '@/store/guilds'
import { useAppStore } from '@/store/app'
import { DM_SETTINGS, ownMuted } from '@/lib/muting'
import { matches, readable, type Suggestion, type Trigger } from '@/lib/autocomplete'
import type { PickEnv } from '@/lib/commands'
import { commandInvocation, mergeEphemeral } from '@/lib/interactions'
import { MentionPopup } from './MentionPopup'
import { useMentions } from './useMentions'
import { CommandForm, SlashPopup } from './SlashCommands'
import { useComponentEnv, type CommandSource } from './useCommandIndex'
import { useSlashCommands } from './useSlashCommands'
import { FormatToolbar } from './FormatToolbar'
import { useFormatToolbar } from './useFormatToolbar'
import { useChatReading } from './useChatReading'
import { useAutosize } from '@/lib/useAutosize'
import { draftPicks, useDraft, useDraftsStore } from '@/store/drafts'
import { useAuthStore } from '@/store/auth'
import { attachmentLimit, messageLimit } from '@/lib/upload'
import { uploadGif } from '@/lib/sendGif'
import { channelName, displayName, typingUsers, useSocialStore } from '@/store/social'
import { describeError, publicUser } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { AttachmentStrip } from './AttachmentStrip'
import { filesFromPaste, useUploads } from './useUploads'
import { cx } from '@/lib/cx'
import { ChannelType, MessageType, type Message, type User } from '@/types/api'
import { Avatar } from '../Avatar'
import { GroupAvatar } from '../GroupAvatar'
import { StatusIcon } from '../StatusIcon'
import { STATUS_LABEL, isOnline } from '@/lib/presence'
import { avatarUrl } from '@/lib/avatar'
import { userContextHandler } from '@/store/contextMenu'
import { MessageRow } from './MessageRow'
import { SystemMessage } from './SystemMessage'
import { DMWelcome } from './DMWelcome'
import { CallPanel } from '../voice/CallPanel'
import { useVoiceStore } from '@/store/voice'
import { ReportModal } from '../ReportModal'
import { ComposerPlus } from './ComposerPlus'
import { CreatePollModal } from './CreatePollModal'
import { VoiceRecordBar } from './VoiceRecorder'
import { useVoiceRecorder } from './useVoiceRecorder'
import { canRecordVoice } from '@/lib/voiceMessage'
import type { ReportTarget } from '@/lib/reportApi'
import { formatDay } from '@/lib/format'
import chatStyles from '../ChatView.module.css'
import styles from './DMChat.module.css'
import { coarsePointer, useIsMobile } from '@/lib/mobile'
import { MobileBack } from '@/components/MobileBack'
import { JumpToPresent } from './JumpToPresent'
import { useMessageJump } from './useMessageJump'
import { StyledName } from '../StyledName'

const GROUP_WINDOW = 7 * 60 * 1000
const SYSTEM_TYPES = new Set<number>([MessageType.RecipientAdd, MessageType.RecipientRemove, MessageType.Call, MessageType.ChannelNameChange, MessageType.ChannelPinned, MessageType.PollResult])

export function DMChat({ channelId }: { channelId: string }) {
  const me = useAuthStore((s) => s.user)
  const channel = useSocialStore((s) => s.channels[channelId])
  const messages = useSocialStore((s) => s.messages[channelId])
  const stale = useSocialStore((s) => channelId in s.stale)
  // ответы бота «только вам» — в ленте среди остальных, но не в истории беседы
  const ephemeral = useSocialStore((s) => s.ephemeral[channelId])
  const shown = useMemo(() => mergeEphemeral(messages ?? [], ephemeral), [messages, ephemeral])
  const hasOlder = useSocialStore((s) => s.hasOlder[channelId])
  const loading = useSocialStore((s) => s.loading[channelId])
  const typingMap = useSocialStore((s) => s.typing[channelId])
  const currentUserId = useSocialStore((s) => s.currentUserId)
  const typingIds = useMemo(() => typingUsers(typingMap, currentUserId), [typingMap, currentUserId])
  const presences = useSocialStore((s) => s.presences)
  const { loadMessages, sendMessage, retryMessage, editMessage, deleteMessage, suppressEmbeds, ack, typingPing, renameGroup, toggleReaction, pinMessage } = useSocialStore()
  const allGuilds = useGuildStore((s) => s.guilds)
  const setJumpTarget = useAppStore((s) => s.setJumpTarget)
  const customEmoji = useMemo(() => Object.values(allGuilds).map((g) => ({ guild: g.name, emojis: g.emojis ?? [] })), [allGuilds])
  const dmSearch = useAppStore((s) => s.dmSearch)
  const setDMSearch = useAppStore((s) => s.setDMSearch)
  const searchOpen = dmSearch?.channelId === channelId
  const mobile = useIsMobile()
  const searchCoversChat = mobile && searchOpen
  const [pinsOpen, setPinsOpen] = useState(false)
  const [headerMenu, setHeaderMenu] = useState<{ x: number; y: number } | null>(null)
  const headerMenuButton = useRef<HTMLButtonElement | null>(null)
  const closeHeaderMenu = useCallback(() => setHeaderMenu(null), [])
  const call = useSocialStore((s) => s.calls[channelId])
  const voiceConnection = useVoiceStore((s) => s.connection)
  const joinVoice = useVoiceStore((s) => s.join)
  const inThisCall = voiceConnection?.channelId === channelId
  const [report, setReport] = useState<ReportTarget | null>(null)
  const [pollOpen, setPollOpen] = useState(false)

  const [draft, setDraft] = useDraft(channelId)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [error, setError] = useState<string | null>(null)
  const voice = useVoiceRecorder(channelId, setError)
  const plus = useAuthStore((s) => s.user?.premium_type === 2)
  const uploads = useUploads(channelId, attachmentLimit(Boolean(plus)))
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  // участники группы — в правой колонке приложения, как у сервера: на телефоне это слой поверх чата
  const showMembers = useAppStore((s) => s.showMembers)
  const toggleMembers = useAppStore((s) => s.toggleMembers)
  const membersVisible = showMembers && !searchOpen
  const toggleDMPanel = () => {
    if (searchOpen) { setDMSearch(null); if (!showMembers) toggleMembers() }
    else toggleMembers()
  }
  const [renaming, setRenaming] = useState<string | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const stickToBottom = useRef(true)
  const latestId = useCallback(() => useSocialStore.getState().channels[channelId]?.last_message_id, [channelId])
  // до эффекта первой загрузки: окно вокруг сообщения должно начать грузиться раньше неё
  const { highlight, detached, toPresent, jumping } = useMessageJump(channelId, scrollerRef, stickToBottom, latestId)
  const muted = useGuildStore((s) => ownMuted(s.settings[DM_SETTINGS], channelId))
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const reading = useChatReading(me?.id, channelId, scrollerRef, stickToBottom, detached, jumping, !searchCoversChat, (err) => setError(err instanceof Error ? err.message : describeError(err).message))
  const goLatest = () => void reading.toLatest(toPresent).catch((err) => setError(err instanceof Error ? err.message : describeError(err).message))
  const reply = useCallback((message: Message) => {
    setReplyTo(message)
    textareaRef.current?.focus({ preventScroll: true })
  }, [])
  const sending = useRef(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const picker = usePicker()
  const [sendingGif, setSendingGif] = useState(false)
  const gifsOn = useGifsEnabled()
  const sendSticker = useSocialStore((s) => s.sendSticker)
  const stickerGroups = useMemo(() => Object.values(allGuilds).filter((g) => g.stickers?.length).map((g) => ({ guild: g.name, stickers: g.stickers ?? [] })), [allGuilds])

  const isGroup = channel?.type === ChannelType.GroupDM
  const recipient = !isGroup ? channel?.recipients?.[0] : undefined
  const users = useMemo(() => {
    const map: Record<string, User> = {}
    if (me) map[me.id] = publicUser(me)
    for (const u of channel?.recipients ?? []) map[u.id] = u
    return map
  }, [me, channel])

  const suggest = useCallback(
    (tr: Trigger): Suggestion[] => {
      if (tr.kind === ':') {
        const custom: Suggestion[] = Object.values(allGuilds)
          .flatMap((g) => (g.emojis ?? []).map((e) => ({ e, guild: g })))
          .filter(({ e }) => e.name.toLowerCase().includes(tr.query.toLowerCase()))
          .slice(0, 6)
          .map(({ e, guild: g }) => ({ key: `custom:${e.id}`, kind: 'emoji' as const, label: `:${e.name}:`, hint: g.name, emojiUrl: e.url ?? emojiImageUrl(e.id, e.animated), insert: customEmojiToken(e), text: `:${e.name}:` }))
        const unicode: Suggestion[] = searchUnicode(tr.query, 6).map((it) => ({ key: `u:${it.char}`, kind: 'emoji' as const, label: it.char, hint: it.keys.split(' ')[0], emoji: it.char, insert: it.char }))
        return [...custom, ...unicode]
      }
      if (tr.kind === '#') return []
      return (channel?.recipients ?? [])
        .filter((u) => matches(tr.query, u.username, u.global_name))
        .map((u) => ({ key: u.id, kind: 'user' as const, label: displayName(u), hint: `@${u.username}`, insert: `<@${u.id}>`, text: readable('user', displayName(u)), avatar: avatarUrl(u.id, u.avatar) }))
    },
    [channel, allGuilds],
  )
  const mentions = useMentions({ textarea: textareaRef, draft, setDraft, suggest, picked: draftPicks(channelId) })
  // команды — только в личке с ботом: его глобальные команды, разрешённые в личке
  const bot = recipient?.bot ? recipient : undefined
  const pickEnv = useMemo<PickEnv>(
    () => ({
      users: [...(me ? [me] : []), ...(channel?.recipients ?? [])].map((u) => ({ id: u.id, name: displayName(u), username: u.username, bot: Boolean(u.bot), avatar: u.avatar })),
      roles: [],
      channels: [],
    }),
    [me, channel],
  )
  const commandSource = useMemo<CommandSource>(() => (bot ? { dmChannelId: channelId } : null), [bot, channelId])
  const slash = useSlashCommands({
    source: commandSource,
    enabled: Boolean(bot),
    guildId: null,
    channelId,
    draft,
    setDraft,
    textarea: textareaRef,
    env: pickEnv,
    maxFileSize: attachmentLimit(Boolean(plus)),
    onSend: () => {
      stickToBottom.current = true
    },
  })
  const interact = useComponentEnv({ source: commandSource, guildId: null, env: pickEnv })
  // поле растёт раньше, чем встаёт панель форматирования: она меряет поле уже новой высоты
  useAutosize(textareaRef, draft)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const format = useFormatToolbar(textareaRef, { toolbarRef, hidden: mentions.items.length > 0 || slash.picker !== null, onApply: mentions.close })

  /** Эмодзи из панели уходит в поле ввода на место курсора, а не отправляется сразу. */
  const putEmoji = (e: { id: string | null; name: string; animated?: boolean }) => {
    const el = textareaRef.current
    const token = e.id ? customEmojiToken({ id: e.id, name: e.name, ...(e.animated ? { animated: true } : {}) }) : e.name
    const at = el ? el.selectionStart : draft.length
    const to = el ? el.selectionEnd : draft.length
    const { text, caret } = insertToken(draft, at, to, token)
    setDraft(text)
    if (coarsePointer) picker.close()
    el?.focus({ preventScroll: true })
    requestAnimationFrame(() => {
      el?.setSelectionRange(caret, caret)
    })
  }

  // после переподключения — перечитать конец истории: пропущенное за перерыв само не придёт
  useEffect(() => {
    if (!messages || stale) void loadMessages(channelId)
  }, [channelId, messages, stale, loadMessages])

  // прочитано: когда канал открыт и вкладка видна
  useEffect(() => {
    if (!messages?.length || searchCoversChat || !reading.atLatest) return
    const lastId = [...messages].reverse().find((m) => !m.pending)?.id
    const mark = () => {
      if (document.visibilityState === 'visible') void ack(channelId, lastId)
    }
    mark()
    document.addEventListener('visibilitychange', mark)
    return () => document.removeEventListener('visibilitychange', mark)
  }, [channelId, messages, ack, searchCoversChat, reading.atLatest])

  const onScroll = () => {
    const el = scrollerRef.current
    if (!el) return
    reading.onScroll()
    if (!reading.ready) return
    if (el.scrollTop < 120 && hasOlder && !loading && messages?.length) {
      const first = messages.find((m) => !m.pending)
      if (first) {
        void loadMessages(channelId, first.id).catch((err) => setError(describeError(err).message))
      }
    }
  }

  const submit = async () => {
    if (sending.current || reading.busy) return
    const content = draft.trim()
    if (!content && uploads.items.length === 0) return
    setError(null)
    const reply = replyTo
    const sentFiles = uploads.items.map((file) => file.id)
    sending.current = true
    setSendingMessage(true)
    try {
      await reading.toLatest(toPresent)
      const refs = await uploads.upload(channelId)
      if ((useDraftsStore.getState().drafts[channelId] ?? '') === draft) setDraft('')
      setReplyTo((current) => current?.id === reply?.id ? null : current)
      uploads.clear(sentFiles)
      await sendMessage(channelId, mentions.toServer(content), reply ?? undefined, refs)
    } catch (err) {
      setError(err instanceof Error && !(err instanceof ApiError) ? err.message : describeError(err).message)
    } finally {
      sending.current = false
      setSendingMessage(false)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slash.onTextKeyDown(e)) return
    if (mentions.onKeyDown(e)) return
    if (format.onKeyDown(e)) return
    if (e.key === 'Enter' && !e.shiftKey && !coarsePointer && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
    if (e.key === 'Escape' && replyTo) setReplyTo(null)
  }

  const submitRename = async () => {
    if (renaming === null) return
    const name = renaming.trim()
    setRenaming(null)
    if (name === (channel?.name ?? '')) return
    try {
      await renameGroup(channelId, name)
    } catch (err) {
      setError(describeError(err).message)
    }
  }

  const title = channel ? channelName(channel) : 'Личные сообщения'
  const recipientStatus = recipient ? presences[recipient.id] : undefined
  const status = isOnline(recipientStatus) ? recipientStatus : 'offline'
  const bellLabel = muted ? 'Включить уведомления' : isGroup ? 'Заглушить группу' : 'Заглушить беседу'
  const toggleMute = () => {
    void updateSettings(DM_SETTINGS, { channel_overrides: { [channelId]: { muted: !muted } } }).catch((err: unknown) => setError(describeError(err).message))
  }

  return (
    <div className={styles.layout}>
      <main
        className={cx(chatStyles.chat, styles.main)}
        aria-label={isGroup ? `Группа ${title}` : `Личные сообщения с ${title}`}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault()
            setDragging(true)
          }
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          setDragging(false)
          if (e.dataTransfer.files.length) {
            e.preventDefault()
            uploads.add(e.dataTransfer.files)
          }
        }}
      >
        {dragging ? <div className={styles.dropOverlay} aria-hidden="true">Отпустите, чтобы прикрепить файлы</div> : null}
        <header className={chatStyles.header}>
          <MobileBack />
          {isGroup ? <Users size={24} className={chatStyles.hash} /> : <AtSign size={24} className={cx(chatStyles.hash, styles.dmHash)} />}
          {isGroup && renaming !== null ? (
            <input
              className={styles.renameInput}
              value={renaming}
              maxLength={100}
              autoFocus
              aria-label="Название группы"
              placeholder="Название группы"
              onChange={(e) => setRenaming(e.target.value)}
              onBlur={() => void submitRename()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitRename()
                if (e.key === 'Escape') setRenaming(null)
              }}
            />
          ) : (
            <h1 className={cx(chatStyles.title, recipient && styles.dmTitle)}>
              {recipient ? (
                <button type="button" className={styles.titleUser} data-tooltip="Профиль" onClick={userContextHandler(recipient.id, { user: recipient })}>
                  <Avatar initial={title.slice(0, 1).toUpperCase()} color="#5865F2" size={24} src={avatarUrl(recipient.id, recipient.avatar)} decoration={recipient.avatar_decoration_data} />
                  <StyledName name={title} style={recipient.display_name_styles} className={styles.titleName} />
                </button>
              ) : title}
            </h1>
          )}
          {isGroup && renaming === null ? (
            <button type="button" className={styles.renameButton} data-tooltip="Переименовать группу" aria-label="Переименовать группу" onClick={() => setRenaming(channel?.name ?? '')}>
              <Pencil size={14} />
            </button>
          ) : null}
          {recipient ? (
            <span className={styles.statusDot} data-tooltip={STATUS_LABEL[status]}>
              <StatusIcon status={status} label={STATUS_LABEL[status]} />
            </span>
          ) : null}
          <div className={chatStyles.tools}>
            <button id={`dm-search-button-${channelId}`} type="button" className={cx(chatStyles.iconButton, searchOpen && styles.toolActive)} data-tooltip="Поиск по беседе" aria-label="Поиск по беседе" aria-expanded={searchOpen} aria-controls={searchOpen ? `dm-search-${channelId}` : undefined} onClick={() => setDMSearch(searchOpen ? null : { channelId, query: '' })}>
              <Search size={20} />
            </button>
            <button type="button" className={cx(chatStyles.iconButton, styles.desktopTool, muted && styles.toolMuted)} data-tooltip={bellLabel} aria-label={bellLabel} aria-pressed={muted} onClick={toggleMute}>
              {muted ? <BellOff size={20} /> : <Bell size={20} />}
            </button>
            <button type="button" className={cx(chatStyles.iconButton, styles.desktopTool, pinsOpen && styles.toolActive)} data-tooltip="Закреплённые сообщения" aria-label="Закреплённые сообщения" aria-pressed={pinsOpen} onClick={() => setPinsOpen((v) => !v)}>
              <Pin size={20} />
            </button>
            {pinsOpen ? <PinnedPanel channelId={channelId} onJump={(id) => setJumpTarget({ channelId, messageId: id })} onClose={() => setPinsOpen(false)} /> : null}
            {!call && !inThisCall ? (
              <button type="button" className={chatStyles.iconButton} data-tooltip="Позвонить" aria-label="Позвонить" onClick={() => void joinVoice(null, channelId).catch(() => undefined)}>
                <Phone size={20} />
              </button>
            ) : null}
            {recipient ? (
              <button type="button" className={cx(chatStyles.iconButton, styles.desktopTool)} data-tooltip="Пожаловаться на пользователя" aria-label="Пожаловаться на пользователя" onClick={() => setReport({ type: 'user', id: recipient.id, label: `@${recipient.username}` })}>
                <Flag size={20} />
              </button>
            ) : null}
            {isGroup ? (
              <button type="button" className={cx(chatStyles.iconButton, styles.desktopTool, membersVisible && styles.toolActive)} data-tooltip={membersVisible ? 'Скрыть участников' : 'Показать участников'} aria-label="Участники" aria-pressed={membersVisible} onClick={toggleDMPanel}>
                <Users size={22} />
              </button>
            ) : null}
            {/* профиль собеседника справа, как в Discord: та же колонка, что у участников группы */}
            {recipient ? (
              <button type="button" className={cx(chatStyles.iconButton, styles.desktopTool, membersVisible && styles.toolActive)} data-tooltip={membersVisible ? 'Скрыть профиль' : 'Показать профиль'} aria-label="Профиль собеседника" aria-pressed={membersVisible} onClick={toggleDMPanel}>
                <CircleUserRound size={21} />
              </button>
            ) : null}
            {mobile ? <button ref={headerMenuButton} type="button" className={chatStyles.iconButton} aria-label="Ещё действия в беседе" aria-haspopup="menu" aria-expanded={Boolean(headerMenu)} onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              setHeaderMenu(headerMenu ? null : { x: r.right, y: r.bottom + 4 })
            }}><MoreHorizontal size={20} /></button> : null}
          </div>
          {mobile && headerMenu ? (
            <ContextMenu {...headerMenu} align="right" anchorRef={headerMenuButton} label="Действия в беседе" onClose={closeHeaderMenu}>
              <MenuItem icon={muted ? <BellOff size={16} /> : <Bell size={16} />} label={bellLabel} onClick={() => { closeHeaderMenu(); toggleMute() }} />
              <MenuItem icon={<Pin size={16} />} label="Закреплённые сообщения" onClick={() => { closeHeaderMenu(); setPinsOpen((v) => !v) }} />
              <MenuItem icon={isGroup ? <Users size={16} /> : <CircleUserRound size={16} />} label={isGroup ? 'Участники' : 'Профиль собеседника'} onClick={() => { closeHeaderMenu(); toggleDMPanel() }} />
              {recipient ? <MenuItem icon={<Flag size={16} />} label="Пожаловаться на пользователя" onClick={() => { closeHeaderMenu(); setReport({ type: 'user', id: recipient.id, label: `@${recipient.username}` }) }} /> : null}
            </ContextMenu>
          ) : null}
        </header>
        {call || inThisCall ? <CallPanel channelId={channelId} users={users} /> : null}

        <div className={chatStyles.messageViewport}>
          <div className={cx(chatStyles.messages, 'scroller')} ref={reading.ref} onScroll={onScroll}>
            <div className={chatStyles.messagesInner}>
              {!hasOlder && recipient ? <DMWelcome user={recipient} /> : null}
              {!hasOlder && isGroup && channel ? (
                <div className={chatStyles.welcome}>
                  <GroupAvatar recipients={channel.recipients} size={80} />
                  <h2 className={chatStyles.welcomeTitle}>{title}</h2>
                  <p className={chatStyles.welcomeText}>
                    Это начало группы. Участники: <strong>{[me ? displayName(me) : 'вы', ...(channel.recipients ?? []).map(displayName)].join(', ')}</strong>.
                  </p>
                </div>
              ) : null}
              {loading && !messages?.length ? <p className={styles.loading}>Загружаем историю…</p> : null}
              {shown.map((m, i, all) => {
                const prev = all[i - 1]
                const sameDay = prev && new Date(prev.timestamp).toDateString() === new Date(m.timestamp).toDateString()
                const system = SYSTEM_TYPES.has(m.type)
                // ответ на команду начинает группу: над ним строка «использовал(а) /команда», как у ответа
                const continuation = Boolean(
                  prev && sameDay && !system && !SYSTEM_TYPES.has(prev.type) && prev.author.id === m.author.id && (!m.webhook_id || prev.author.username === m.author.username) && !m.referenced_message && !m.message_reference && !commandInvocation(m) &&
                    new Date(m.timestamp).getTime() - new Date(prev.timestamp).getTime() < GROUP_WINDOW,
                )
                return (
                  <div key={m.id} data-message-id={m.id} className={cx(highlight === m.id && chatStyles.highlight)}>
                    {!sameDay ? <div className={chatStyles.dateDivider}><span>{formatDay(new Date(m.timestamp))}</span></div> : null}
                    {system ? (
                      <SystemMessage message={m} users={users} onJump={(id) => setJumpTarget({ channelId, messageId: id })} />
                    ) : (
                      <MessageRow
                        message={m}
                        continuation={continuation}
                        mine={m.author.id === me?.id}
                        users={users}
                        onReply={reply}
                        onEdit={(msg, content) => editMessage(channelId, msg.id, content)}
                        onDelete={(msg) => deleteMessage(channelId, msg.id)}
                        onRetry={(msg) => msg.nonce && void retryMessage(channelId, msg.nonce)}
                        onReport={(msg) => setReport({ type: 'message', id: msg.id, label: `сообщение ${displayName(users[msg.author.id] ?? msg.author)}` })}
                        onSuppress={(msg) => suppressEmbeds(channelId, msg.id)}
                        onReact={(msg, e) => toggleReaction(channelId, msg, e).catch((err) => setError(describeError(err).message))}
                        onPin={(msg, pinned) => pinMessage(channelId, msg.id, pinned).catch((err) => setError(describeError(err).message))}
                        customEmoji={customEmoji}
                        channelId={channelId}
                        onJump={(id) => setJumpTarget({ channelId, messageId: id })}
                        interact={interact}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {reading.showLatest ? <JumpToPresent onClick={goLatest} disabled={reading.busy || Boolean(loading)} /> : null}
        </div>

        <div className={chatStyles.typing} data-active={typingIds.length ? '' : undefined} aria-live="polite">
          {typingIds.length ? (
            <>
              <span className={chatStyles.dots} aria-hidden="true"><i /><i /><i /></span>
              <span>
                <strong>{typingIds.map((id) => (users[id] ? displayName(users[id]!) : 'Кто-то')).join(', ')}</strong>
                {typingIds.length === 1 ? ' печатает…' : ' печатают…'}
              </span>
            </>
          ) : null}
        </div>

        <div className={styles.composerWrap}>
          {sendingGif ? <p className={styles.sending}>Отправляем гифку…</p> : null}
          {slash.error ?? error ?? uploads.error ? <p className={styles.error} role="alert">{slash.error ?? error ?? uploads.error}</p> : null}
          <AttachmentStrip items={uploads.items} onRemove={uploads.remove} disabled={sendingMessage} />
          <MentionPopup items={mentions.items} active={mentions.active} title="Участники" onPick={mentions.pick} onHover={mentions.setActive} />
          <SlashPopup slash={slash} />
          {format.toolbar ? <FormatToolbar ref={toolbarRef} text={draft} {...format.toolbar} /> : null}
          {picker.tab ? (
            <div className={styles.stickerAnchor}>
              <MediaPicker
                tab={picker.tab}
                onTab={picker.select}
                stickers={stickerGroups}
                emoji={customEmoji}
                gifs={gifsOn}
                onClose={picker.close}
                onSticker={(s) => { picker.close(); void reading.toLatest(toPresent).then(() => sendSticker(channelId, s)).catch((err) => setError(err instanceof Error ? err.message : describeError(err).message)) }}
                onGif={(g) => {
                picker.close()
                setSendingGif(true)
                // гифку кладём вложением: в чате должна остаться она сама, а не ссылка
                reading.toLatest(toPresent).then(() => uploadGif(channelId, g, attachmentLimit(Boolean(plus))))
                  .then((refs) => sendMessage(channelId, '', undefined, refs))
                  .catch((err) => setError(err instanceof Error ? err.message : describeError(err).message))
                  .finally(() => setSendingGif(false))
              }}
                onEmoji={putEmoji}
              />
            </div>
          ) : null}
          {voice.rec ? <VoiceRecordBar rec={voice.rec} busy={voice.busy} onSend={() => void voice.send()} onCancel={voice.cancel} /> : null}
          {replyTo ? (
            <div className={styles.replyBar}>
              <span>Ответ пользователю <strong>{displayName(users[replyTo.author.id] ?? replyTo.author)}</strong></span>
              <button type="button" aria-label="Отменить ответ" onClick={() => setReplyTo(null)}><X size={16} /></button>
            </div>
          ) : null}
          <form className={cx(chatStyles.composer, (replyTo || uploads.items.length > 0) && styles.composerWithReply)} onSubmit={(e) => { e.preventDefault(); if (slash.form) void slash.form.submit(); else void submit() }}>
            {slash.form ? (
              <CommandForm slash={slash} form={slash.form} />
            ) : (
              <ComposerPlus className={chatStyles.attach} disabled={sendingMessage} canAttach canPoll onUpload={() => fileInput.current?.click()} onPoll={() => setPollOpen(true)} onFiles={uploads.add} onGif={gifsOn ? () => picker.toggle('gif') : undefined} onSticker={() => picker.toggle('sticker')} />
            )}
            <input ref={fileInput} type="file" multiple hidden aria-hidden="true" onChange={(e) => { if (e.target.files) uploads.add(e.target.files); e.target.value = '' }} />
            {/* в режиме команды поле ввода прячется, но остаётся в DOM: его хуки держат ссылку на него */}
            <textarea
              ref={textareaRef}
              rows={1}
              hidden={slash.form !== null}
              {...slash.textAria}
              className={chatStyles.input}
              placeholder={isGroup ? `Написать в «${title}»` : `Написать @${recipient?.username ?? ''}`}
              aria-label="Текст сообщения"
              value={draft}
              maxLength={messageLimit(Boolean(plus))}
              onChange={(e) => {
                setDraft(e.target.value)
                mentions.refresh(e.target.value, e.target.selectionStart)
                slash.onTextChange(e.target.value)
                if (e.target.value.trim()) typingPing(channelId)
              }}
              onFocus={slash.onTextFocus}
              onKeyDown={onKeyDown}
              onSelect={format.onSelect}
            onPaste={(e) => {
              const files = filesFromPaste(e)
              if (files.length) {
                e.preventDefault()
                uploads.add(files)
                return
              }
              format.onPaste(e)
            }}
              onBlur={() => {
                setTimeout(mentions.close, 150)
                format.onBlur()
              }}
            />
          {slash.form ? null : (
          <div className={chatStyles.composerTools}>
            {canRecordVoice && (true) ? (
              <button type="button" className={cx(chatStyles.tool, voice.recording && chatStyles.toolActive, (draft.trim() || uploads.items.length > 0) && chatStyles.desktopTool)} data-tooltip={voice.recording ? 'Отправить голосовое' : 'Голосовое сообщение'} aria-label={voice.recording ? 'Отправить голосовое' : 'Записать голосовое'} aria-pressed={voice.recording} disabled={voice.busy} onClick={() => void (voice.recording ? voice.send() : voice.start())}>
                <Mic size={22} />
              </button>
            ) : null}
            {gifsOn ? (
              <button type="button" data-picker-trigger className={cx(chatStyles.tool, chatStyles.desktopTool)} data-tooltip="Гифки" aria-label="Гифки" aria-expanded={picker.tab === 'gif'} onClick={() => picker.toggle('gif')}>
                <ImagePlay size={22} />
              </button>
            ) : null}
            <button type="button" data-picker-trigger className={cx(chatStyles.tool, chatStyles.desktopTool)} data-tooltip="Стикеры" aria-label="Стикеры" aria-expanded={picker.tab === 'sticker'} onClick={() => picker.toggle('sticker')}>
              <StickerIcon size={22} />
            </button>
            <button type="button" data-picker-trigger className={chatStyles.tool} data-tooltip="Эмодзи" aria-label="Эмодзи" aria-expanded={picker.tab === 'emoji'} onClick={() => { if (coarsePointer) textareaRef.current?.blur(); picker.toggle('emoji') }}>
              <Smile size={22} />
            </button>
            {(draft.trim() || uploads.items.length > 0) ? <button type="submit" className={cx(chatStyles.tool, chatStyles.mobileSend)} aria-label="Отправить сообщение" disabled={sendingMessage || reading.busy} onMouseDown={(e) => e.preventDefault()}><Send size={22} /></button> : null}
          </div>
          )}
          </form>
        </div>
      </main>
      {report ? <ReportModal target={report} onClose={() => setReport(null)} /> : null}
      {pollOpen ? <CreatePollModal channelId={channelId} custom={customEmoji} onClose={() => setPollOpen(false)} /> : null}
    </div>
  )
}
